/* @flow */

import { ChartModel } from './chart.model';
import { CANVAS_WIDTH, CHART_PREVIEW_REC } from './constants';
import { EventTarget } from './utils/event-target';

export class ChartControls extends EventTarget {
    chart: ChartModel;
    container: HTMLElement;
    selection: HTMLElement;
    leftResize: HTMLElement;
    rightResize: HTMLElement;

    constructor (container: HTMLElement, chart: ChartModel) {
        super();
        this.container = container;
        this.chart = chart;
        this._render();
        this.update();
    }

    _render () {
        this.selection = document.createElement('div');
        this.selection.dataset.type = 'move';
        this.selection.style.position = 'absolute';
        this.selection.style.bottom = '0';
        this.selection.style.cursor = 'grab';
        this.container.appendChild(this.selection);

        this.leftResize = document.createElement('div');
        this.leftResize.dataset.type = 'resize-left';
        this.leftResize.className = 'resize-left';
        this.leftResize.style.position = 'absolute';
        this.leftResize.style.cursor = 'col-resize';
        this.leftResize.style.bottom = '0';
        this.leftResize.style.top = '0';
        this.leftResize.style.left = '0';
        this.leftResize.style.width = '10px';
        this.selection.appendChild(this.leftResize);

        this.rightResize = document.createElement('div');
        this.rightResize.dataset.type = 'resize-right';
        this.rightResize.className = 'resize-right';
        this.rightResize.style.position = 'absolute';
        this.rightResize.style.cursor = 'col-resize';
        this.rightResize.style.bottom = '0';
        this.rightResize.style.top = '0';
        this.rightResize.style.right = '0';
        this.rightResize.style.width = '10px';
        this.selection.appendChild(this.rightResize);
        this._registerEvents();
    }

    update () {
        this.selection.style.height = CHART_PREVIEW_REC.y2 - CHART_PREVIEW_REC.y1 + 'px';
        const step = CANVAS_WIDTH / this.chart.row.data.length;
        this.selection.style.left = (step * this.chart.selectStart) + 'px';
        this.selection.style.width = ((step * this.chart.selectEnd) - (step * this.chart.selectStart)) + 'px';
    }

    _registerEvents () {
        let moveOffset = 0;
        let currentEvent = null;

        const onTouchStart = (event: TouchEvent) => {
            event.stopPropagation();
            event.preventDefault();
            if (event.target instanceof HTMLElement) {
                currentEvent = event.target.dataset.type;
            }
            let rect = null;
            if (currentEvent === 'move') {
                rect = this.selection.getBoundingClientRect();
            } else if (currentEvent === 'resize-left') {
                rect = this.leftResize.getBoundingClientRect();
            } else if (currentEvent === 'resize-right') {
                rect = this.rightResize.getBoundingClientRect();
            }
            moveOffset = event.touches[0].pageX - rect.left;
            window.addEventListener('touchmove', onTouchMove, false);
            window.addEventListener('touchend', onMoveEnd, false);

        };

        const onMouseStart = (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            moveOffset = event.offsetX;
            window.addEventListener('mousemove', onMouseMove, false);
            window.addEventListener('mouseup', onMoveEnd, false);
            if (event.target instanceof HTMLElement) {
                currentEvent = event.target.dataset.type;
            }
        };

        const onMouseMove = (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            this._move(event.pageX - moveOffset, currentEvent);
        };

        const onTouchMove = (event: TouchEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const x = event.touches[0].pageX;
            this._move(x - moveOffset, currentEvent);
        };

        const onMoveEnd = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mouseup', onMoveEnd);
            window.removeEventListener('touchend', onMoveEnd);
        };

        this.selection.addEventListener('mousedown', onMouseStart, false);
        this.selection.addEventListener('touchstart', onTouchStart, false);
        this.leftResize.addEventListener('mousedown', onMouseStart, false);
        this.leftResize.addEventListener('touchstart', onTouchStart, false);
        this.rightResize.addEventListener('mousedown', onMouseStart, false);
        this.rightResize.addEventListener('touchstart', onTouchStart, false);
    }

    _move (offset: number, eventName: ?string) {
        let left = offset - this.container.offsetLeft;
        const step = CANVAS_WIDTH / this.chart.row.data.length;
        let start = this.chart.selectStart;
        let end = this.chart.selectEnd;
        switch (eventName) {
        case 'move':
            start = Math.round(left / step);
            start = start > 0 ? start : 0;
            const delta = this.chart.selectEnd - this.chart.selectStart;
            start = start + delta < this.chart.row.data.length ? start : this.chart.row.data.length - delta;
            end = start + delta;
            break;
        case 'resize-left':
            start = Math.round(left / step);
            start = start > 0 ? start : 0;
            start = (end - start) * step > 30 ? start : end - (30 / step);
            this.chart.selectStart = start;
            break;
        case 'resize-right':
            end = Math.round(left / step);
            end = end > this.chart.row.data.length ? this.chart.row.data.length : end;
            end = (end - start) * step > 30 ? end : start + (30 / step);
            break;
        }
        this.chart.selectStart = start;
        this.chart.selectEnd = end;
        this.fireEvent('move', { start, end });
        this.update();
    }
}
